/**
 * PFM service — Performance Measures.
 *
 * Every PFM API call funnels through here, per house convention: components
 * never talk to apiService directly.
 *
 * Business rules: docs/PFM_Performance_Measures_Business_Rules.html
 */
import apiClient from './apiService';

const BASE = '/api/v1/pfm';

// --- Registry (seeded, read-only) -------------------------------------------

export interface PfmDimension {
  dimension_code: string;
  display_name: string;
  anchor_table: string;
  label_column: string;
  parent_column: string | null;
  /** Only a hierarchical dimension may roll up (PFM-SCOPE-2). v1: Geography alone. */
  supports_hierarchy: boolean;
  sort_order: number;
}

export interface PfmOperator {
  operator_code: string;
  display_name: string;
  operator_family: 'aggregation' | 'binary' | 'temporal';
  operand_arity: number;
  /** 'ignore' only for COUNT, which measures reporting completeness itself. */
  missing_input_behaviour: 'stale' | 'ignore';
  result_unit_mode: 'inherit' | 'ratio' | 'percent';
  needs_node_set: boolean;
  description?: string | null;
  sort_order: number;
}

export interface PfmNode {
  id: number;
  label: string;
  parent_id: number | null;
}

// --- Measures ----------------------------------------------------------------

export type MeasureType = 'base' | 'derived';
export type ScopeExtent = 'SELF' | 'ROLLUP';
export type Direction = 'LOWER_IS_BETTER' | 'HIGHER_IS_BETTER' | 'TARGET_IS_BETTER';

export interface PfmOperand {
  id?: number;
  operand_measure_id: number;
  operand_measure_name?: string;
  /** Order is semantic: 1 = A, 2 = B for A/B and A-B (PFM-OP-4). */
  operand_position: number;
  operand_role: 'value' | 'weight';
}

export interface PfmMeasure {
  id: number;
  entity_id?: number;
  measure_name: string;
  description?: string | null;
  code?: string | null;
  measure_type: MeasureType;
  unit?: string | null;
  decimal_places: number;
  primary_dimension_code: string;
  secondary_dimension_code?: string | null;
  scope_extent: ScopeExtent;
  periodicity_id?: number | null;
  periodicity_name?: string | null;
  operator_code?: string | null;
  direction: Direction;
  target_value?: number | null;
  requires_submission_review: boolean;
  operands?: PfmOperand[];
  is_synthetic?: boolean;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  is_active: number;
}

export type MeasurePayload = Partial<Omit<PfmMeasure, 'id' | 'operands'>> & {
  operands?: Array<Pick<PfmOperand, 'operand_measure_id' | 'operand_position' | 'operand_role'>>;
};

// --- Bands -------------------------------------------------------------------

export type BandSemantic = 'desirable' | 'acceptable' | 'undesirable' | 'neutral';

export interface PfmBand {
  id?: number;
  measure_id?: number;
  band_name: string;
  /** null = unbounded. Intervals are half-open [lower, upper). */
  lower_bound: number | null;
  upper_bound: number | null;
  semantic: BandSemantic;
  sort_order: number;
  workflow_state?: string;
  is_active?: number;
}

// --- Assignments --------------------------------------------------------------

export interface PfmAssignment {
  id: number;
  measure_id: number;
  measure_name?: string;
  primary_dimension_code: string;
  primary_node_id: number;
  primary_node_label?: string | null;
  secondary_dimension_code?: string | null;
  secondary_node_id?: number | null;
  secondary_node_label?: string | null;
  scope_extent: ScopeExtent;
  responsible_user_id: number;
  responsible_user_name?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  workflow_state?: string;
  is_active: number;
}

// --- Submissions ---------------------------------------------------------------

export interface PfmSubmission {
  id: number;
  assignment_id: number;
  measure_id?: number;
  measure_name?: string;
  unit?: string | null;
  decimal_places?: number;
  primary_node_label?: string | null;
  secondary_node_label?: string | null;
  period_start: string;
  period_end: string;
  value?: number | null;
  status: 'pending' | 'submitted' | 'approved' | 'rejected';
  note?: string | null;
  previous_value?: number | null;
  requires_submission_review?: boolean;
}

// --- Values ----------------------------------------------------------------------

export type ValueStatus = 'fresh' | 'stale' | 'undefined' | 'superseded';

export interface PfmValue {
  id: number;
  measure_id: number;
  measure_name?: string;
  unit?: string | null;
  decimal_places?: number;
  primary_node_id: number;
  primary_node_label?: string | null;
  secondary_node_label?: string | null;
  period_start: string;
  period_end: string;
  value: number | null;
  status: ValueStatus;
  /** Which ingredients were missing — never show a stale value without it. */
  stale_reason?: string | null;
  band_id?: number | null;
  band_name?: string | null;
  band_semantic?: BandSemantic | null;
}

export interface PfmLineageRow {
  id: number;
  input_measure_id?: number | null;
  input_measure_name?: string | null;
  input_node_id?: number | null;
  input_node_label?: string | null;
  was_missing: boolean;
}

class PfmService {
  // --- registry ---
  async getDimensions(): Promise<PfmDimension[]> {
    const r = await apiClient.get(`${BASE}/registry/dimensions`);
    return r.data?.data || [];
  }

  async getOperators(): Promise<PfmOperator[]> {
    const r = await apiClient.get(`${BASE}/registry/operators`);
    return r.data?.data || [];
  }

  async getNodes(dimensionCode: string): Promise<{ dimension: PfmDimension; nodes: PfmNode[] }> {
    const r = await apiClient.get(`${BASE}/registry/nodes/${dimensionCode}`);
    return { dimension: r.data?.dimension, nodes: r.data?.data || [] };
  }

  // --- measures ---
  async getMeasures(params?: { measure_type?: MeasureType; dimension_code?: string }): Promise<PfmMeasure[]> {
    const r = await apiClient.get(`${BASE}/measures`, { params });
    return r.data?.data || [];
  }

  async getMeasure(id: number): Promise<PfmMeasure> {
    const r = await apiClient.get(`${BASE}/measures/${id}`);
    return r.data?.data;
  }

  async createMeasure(payload: MeasurePayload): Promise<PfmMeasure> {
    const r = await apiClient.post(`${BASE}/measures`, payload);
    return r.data?.data;
  }

  async updateMeasure(id: number, payload: MeasurePayload): Promise<PfmMeasure> {
    const r = await apiClient.put(`${BASE}/measures/${id}`, payload);
    return r.data?.data;
  }

  async deactivateMeasure(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/measures/${id}`);
  }

  // --- bands ---
  async getBands(measureId: number): Promise<PfmBand[]> {
    const r = await apiClient.get(`${BASE}/measures/${measureId}/bands`);
    return r.data?.data || [];
  }

  /** Bands are replaced as a SET so contiguity can be validated (PFM-BAND-1). */
  async replaceBands(measureId: number, bands: PfmBand[]): Promise<PfmBand[]> {
    const r = await apiClient.put(`${BASE}/measures/${measureId}/bands`, { bands });
    return r.data?.data || [];
  }

  // --- assignments ---
  async getAssignments(measureId?: number): Promise<PfmAssignment[]> {
    const r = await apiClient.get(`${BASE}/assignments`, {
      params: measureId ? { measure_id: measureId } : undefined,
    });
    return r.data?.data || [];
  }

  async createAssignment(payload: {
    measure_id: number;
    primary_node_id: number;
    secondary_node_id?: number | null;
    scope_extent: ScopeExtent;
    responsible_user_id: number;
  }): Promise<PfmAssignment> {
    const r = await apiClient.post(`${BASE}/assignments`, payload);
    return r.data?.data;
  }

  async updateAssignment(id: number, payload: Partial<{
    responsible_user_id: number; scope_extent: ScopeExtent; effective_to: string;
  }>): Promise<PfmAssignment> {
    const r = await apiClient.put(`${BASE}/assignments/${id}`, payload);
    return r.data?.data;
  }

  async deactivateAssignment(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/assignments/${id}`);
  }

  /**
   * How many staff forms this configuration generates per period (PFM-COLL-1).
   * Shown before activation so the collection burden is visible up front.
   */
  async getFormCount(measureId?: number): Promise<{ forms_per_period: number; people_involved: number }> {
    const r = await apiClient.get(`${BASE}/assignments/form-count`, {
      params: measureId ? { measure_id: measureId } : undefined,
    });
    return { forms_per_period: r.data?.forms_per_period ?? 0, people_involved: r.data?.people_involved ?? 0 };
  }

  // --- submissions (staff) ---
  async getMySubmissions(includeSubmitted = false): Promise<PfmSubmission[]> {
    const r = await apiClient.get(`${BASE}/submissions/mine`, {
      params: { include_submitted: includeSubmitted },
    });
    return r.data?.data || [];
  }

  async submitValue(id: number, value: number, note?: string): Promise<PfmSubmission> {
    const r = await apiClient.put(`${BASE}/submissions/${id}`, { value, note });
    return r.data?.data;
  }

  async reviewSubmission(id: number, approve: boolean, note?: string): Promise<PfmSubmission> {
    const r = await apiClient.patch(`${BASE}/submissions/${id}/review`, { note }, { params: { approve } });
    return r.data?.data;
  }

  /** Count of pending slots — drives the Action Centre badge. */
  async pendingCount(): Promise<number> {
    try {
      const rows = await this.getMySubmissions(false);
      return rows.length;
    } catch {
      return 0;
    }
  }

  async generatePeriod(measureId?: number, periodStart?: string): Promise<number> {
    const r = await apiClient.post(`${BASE}/periods/generate`, {
      measure_id: measureId, period_start: periodStart,
    });
    return r.data?.slots_created ?? 0;
  }

  // --- values / dashboard ---
  async getValues(params?: { measure_id?: number; period_start?: string; status?: string }): Promise<PfmValue[]> {
    const r = await apiClient.get(`${BASE}/values`, { params });
    return r.data?.data || [];
  }

  async getLineage(valueId: number): Promise<{ rows: PfmLineageRow[]; missing: number; total: number }> {
    const r = await apiClient.get(`${BASE}/values/${valueId}/lineage`);
    return {
      rows: r.data?.data || [],
      missing: r.data?.missing_count ?? 0,
      total: r.data?.total_inputs ?? 0,
    };
  }

  async getTrend(measureId: number, nodeId?: number, limit = 24): Promise<PfmValue[]> {
    const r = await apiClient.get(`${BASE}/values/trend`, {
      params: { measure_id: measureId, node_id: nodeId, limit },
    });
    return r.data?.data || [];
  }

  async recompute(measureId?: number, periodStart?: string): Promise<{ rows_written: number; rows_stale: number }> {
    const r = await apiClient.post(`${BASE}/recompute`, {
      measure_id: measureId, period_start: periodStart,
    });
    return { rows_written: r.data?.rows_written ?? 0, rows_stale: r.data?.rows_stale ?? 0 };
  }
}

const pfmService = new PfmService();
export default pfmService;

// =============================================================================
// FORMS  (the packing layer)
// =============================================================================

export interface PfmFormSummary {
  id: number;
  name: string;
  dimension_code: string;
  secondary_dimension_code?: string | null;
}

export interface PfmQuestionRow {
  form_template_id: number;
  form_question: string;
  answer_data_type: string;
  is_required: boolean;
  /** null when the question is captured but feeds no measure. */
  binding_id: number | null;
  measure_id: number | null;
  measure_name: string | null;
  unit: string | null;
  primary_dimension_code: string | null;
}

class PfmFormsService {
  /**
   * Measures this form may pack in: base measures whose PRIMARY dimension
   * matches the form's class, minus those already bound on this form.
   * Derived measures are never offered — they are computed, never collected.
   */
  async pickableMeasures(templateId: number): Promise<{ form: PfmFormSummary; measures: PfmMeasure[] }> {
    const r = await apiClient.get(`${BASE}/forms/${templateId}/pickable-measures`);
    return { form: r.data?.form, measures: r.data?.data || [] };
  }

  /** Every question on the form, with the measure it supplies (if any). */
  async bindings(templateId: number): Promise<{ rows: PfmQuestionRow[]; boundCount: number }> {
    const r = await apiClient.get(`${BASE}/forms/${templateId}/bindings`);
    return { rows: r.data?.data || [], boundCount: r.data?.bound_count ?? 0 };
  }

  async bind(templateId: number, formTemplateId: number, measureId: number): Promise<void> {
    await apiClient.post(`${BASE}/forms/${templateId}/bindings`, null, {
      params: { form_template_id: formTemplateId, measure_id: measureId },
    });
  }

  /** Unbinds only — the measure keeps its history and any dependents. */
  async unbind(bindingId: number): Promise<void> {
    await apiClient.delete(`${BASE}/forms/bindings/${bindingId}`);
  }

  /** Create a performance data form (an audit procedure with the PFM flags set). */
  async createForm(payload: NewFormPayload): Promise<{ id: number }> {
    const r = await apiClient.post(`${BASE}/forms`, payload);
    return r.data?.data;
  }

  /**
   * Add a question, optionally linking an existing measure or creating a new
   * base measure inline — the only birthplace of a base measure (PFM-FORM-2).
   */
  async addQuestion(templateId: number, payload: NewQuestionPayload):
      Promise<{ form_template_id: number; measure_id: number | null }> {
    const r = await apiClient.post(`${BASE}/forms/${templateId}/questions`, payload);
    return r.data?.data;
  }

  /** Removes the question; any measure it supplied is kept. */
  async deleteQuestion(questionId: number): Promise<void> {
    await apiClient.delete(`${BASE}/forms/questions/${questionId}`);
  }
}

export interface NewFormPayload {
  procedure_name: string;
  procedure_description?: string;
  pfm_dimension_code: string;
  pfm_secondary_dimension_code?: string | null;
  cadence?: string | null;
}

export interface NewQuestionPayload {
  form_question: string;
  answer_data_type: string;
  is_required: boolean;
  measure_id?: number | null;
  new_measure_name?: string | null;
  new_measure_unit?: string | null;
  new_measure_decimal_places?: number;
  new_measure_direction?: Direction;
}

export const pfmForms = new PfmFormsService();
